import { GANTT_STATUS } from 'ui/components/ModalLink/constants'
import { api } from '../api'

export const projectsApi = api
  .enhanceEndpoints({
    addTagTypes: ['projectInfo', 'projectUsers'],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      addRemoveProjectsToArchive: builder.mutation({
        query: ({ ids, fromToArchive }) => {
          return {
            url: `/project/archive/${fromToArchive ? 'set' : 'unset'}`,
            method: 'POST',
            body: { ids: ids },
          }
        },
      }),
      getProjectHistory: builder.query({
        query: (slug) => {
          return {
            url: `/project/history/${slug}`,
            method: 'GET',
          }
        },
        transformResponse: (response) =>
          response?.data ?? {
            history: [],
            current_details: [],
          },
      }),
      getProjectInfo: builder.query({
        providesTags: ['projectInfo'],
        query: (slug) => {
          return {
            url: `/project/${slug}`,
            method: 'GET',
          }
        },
        transformResponse: (response) => response?.data || [],
      }),
      updateProjectUsers: builder.query({
        providesTags: ['projectUsers'],
        query: (slug) => {
          return {
            url: `/project/${slug}/user`,
            method: 'GET',
          }
        },
        transformResponse: (response) => response?.data || [],
      }),
      updateProjectUser: builder.mutation({
        keepUnusedDataFor: 5,
        query: ({ slug, userId, roleId, priceId }) => {
          return {
            url: `/project/${slug}/user/${userId}`,
            method: 'PATCH',
            body: {
              role_id: roleId,
              ...(priceId != null ? { pricelist_role_id: priceId } : {}),
            },
          }
        },
        invalidatesTags: ['projectInfo'],
      }),
      deleteProjectUser: builder.mutation({
        keepUnusedDataFor: 5,
        query: ({ slug, userId, roleId }) => {
          return {
            url: `/project/${slug}/user/${userId}`,
            method: 'DELETE',
            body: { role_id: roleId },
          }
        },
        invalidatesTags: ['projectInfo'],
      }),
      updateProject: builder.mutation({
        keepUnusedDataFor: 5,
        query: ({ slug, data }) => {
          return {
            url: `/project/${slug}`,
            method: 'PATCH',
            body: data,
          }
        },
        invalidatesTags: ['projectInfo'],
      }),
      updateProjectWikiLink: builder.mutation({
        query: ({ slug, data }) => ({
          url: `/project/${slug}/wiki`,
          method: 'PATCH',
          body: data,
        }),
        async onQueryStarted({ slug, data }, { dispatch, queryFulfilled }) {
          const patch = dispatch(
            projectsApi.util.updateQueryData(
              'getProjectInfo',
              slug,
              (draft) => {
                draft.wiki_link = data.wiki_link
              }
            )
          )

          try {
            await queryFulfilled
          } catch {
            patch.undo()
          }
        },
        invalidatesTags: ['projectInfo'],
      }),
      updateProjectGanttLink: builder.mutation({
        query: ({ slug, data }) => ({
          url: `/project/${slug}/gantt`,
          method: 'PATCH',
          body: data,
        }),
        async onQueryStarted({ slug, data }, { dispatch, queryFulfilled }) {
          const patch = dispatch(
            projectsApi.util.updateQueryData(
              'getProjectInfo',
              slug,
              (draft) => {
                if (draft?.ganttpro) {
                  draft.ganttpro.link = data.ganttpro_link
                  draft.ganttpro.status = data.ganttpro_is_pwd_protected
                    ? GANTT_STATUS.PROTECTED
                    : GANTT_STATUS.PUBLIC
                }
              }
            )
          )

          try {
            await queryFulfilled
          } catch {
            patch.undo()
          }
        },
        invalidatesTags: ['projectInfo'],
      }),
    }),
  })

export const {
  useAddRemoveProjectsToArchiveMutation,
  useUpdateProjectUserMutation,
  useGetProjectInfoQuery,
  useUpdateProjectMutation,
  useUpdateProjectUsersQuery,
  useDeleteProjectUserMutation,
  useUpdateProjectWikiLinkMutation,
  useUpdateProjectGanttLinkMutation,
  useGetProjectHistoryQuery,
} = projectsApi
